import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select,
    Radio,
    TimePicker, InputNumber
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { cashRegisterService } from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const { Title } = Typography;

// Компонент для создания времени продажи алкоголя
const AlcoholSaleTimeEditor = ({ value, onChange }) => {
    // Конвертация времени в секунды
    const timeToSeconds = (time) => {
        if (!time) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60;
    };

    // Конвертация секунд во время
    const secondsToTime = (seconds) => {
        if (seconds === undefined || seconds === null) return '00:00';
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    // Парсинг входящих данных
    const parseValue = (val) => {
        if (!val) return [];
        if (typeof val === 'string') {
            try {
                return JSON.parse(val);
            } catch (e) {
                return [];
            }
        }
        if (Array.isArray(val)) {
            return val;
        }
        return [];
    };

    const [timeSlots, setTimeSlots] = useState(() => {
        const parsed = parseValue(value);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [{ id: uuidv4(), from: '10:00', to: '22:00' }];
        }

        return parsed.map(slot => ({
            id: uuidv4(),
            from: secondsToTime(slot.from),
            to: secondsToTime(slot.to)
        }));
    });

    // Проверка пересечения времени
    const hasTimeConflict = (newSlot, currentIndex) => {
        const currentFrom = timeToSeconds(newSlot.from);
        const currentTo = timeToSeconds(newSlot.to);

        return timeSlots.some((slot, index) => {
            if (index === currentIndex) return false;

            const slotFrom = timeToSeconds(slot.from);
            const slotTo = timeToSeconds(slot.to);

            return currentFrom < slotTo && currentTo > slotFrom;
        });
    };

    const updateTimeSlots = (newTimeSlots) => {
        setTimeSlots(newTimeSlots);

        // Конвертация в формат API
        const apiFormat = newTimeSlots.map(slot => ({
            from: timeToSeconds(slot.from),
            to: timeToSeconds(slot.to)
        }));

        onChange(apiFormat);
    };

    const handleTimeSlotChange = (index, field, value) => {
        const newTimeSlots = [...timeSlots];
        newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };

        updateTimeSlots(newTimeSlots);
    };

    const removeTimeSlot = (index) => {
        if (timeSlots.length > 1) {
            const newTimeSlots = timeSlots.filter((_, i) => i !== index);
            updateTimeSlots(newTimeSlots);
        }
    };

    return (
        <div>
            {timeSlots.map((slot, index) => {
                const hasConflict = hasTimeConflict(slot, index);

                return (
                    <div
                        key={slot.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 12,
                            gap: 12,
                            padding: hasConflict ? '8px' : '0',
                            backgroundColor: hasConflict ? '#fff2f0' : 'transparent',
                            borderRadius: 6,
                        }}
                    >
                        <TimePicker
                            format="HH:mm"
                            value={slot.from ? dayjs(slot.from, 'HH:mm') : null}
                            onChange={(time) => handleTimeSlotChange(index, 'from', time ? time.format('HH:mm') : '00:00')}
                            placeholder="10:00"
                            style={{ width: 100 }}
                        />

                        <span>—</span>

                        <TimePicker
                            format="HH:mm"
                            value={slot.to ? dayjs(slot.to, 'HH:mm') : null}
                            onChange={(time) => handleTimeSlotChange(index, 'to', time ? time.format('HH:mm') : '23:59')}
                            placeholder="22:00"
                            style={{ width: 100 }}
                        />

                        {timeSlots.length > 1 && (
                            <Button
                                type="text"
                                danger
                                size="small"
                                onClick={() => removeTimeSlot(index)}
                                style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                                ✕
                            </Button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const CashRegisterCreate = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        payment_methods_list: [],
        fiscal_cash_registers: [],
        receipt_width: [],
        tax_rate: []
    });
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchFormData();
        setPageTitle('Создание кассы');

        setRightContent(
            <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Создать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    const fetchFormData = async () => {
        try {
            setLoading(true);
            // Получаем данные для формы
            const formDataResponse = await cashRegisterService.getFormData();

            if (formDataResponse.status === 'success') {
                setFormData(formDataResponse.data);
                console.log('Получены данные для формы:', formDataResponse.data);
            } else {
                message.error('Не удалось загрузить данные для формы');
                navigate('/cash-registers');
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
            message.error('Ошибка при загрузке данных для формы');
            navigate('/cash-registers');
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных кассы
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await cashRegisterService.createCashRegister(
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Касса успешно создана');
                // Переходим на страницу созданной кассы
                navigate(`/cash-registers/${response.data.cash_register.id}`);
            } else {
                message.error(response.message || 'Ошибка при создании кассы');
            }
        } catch (error) {
            console.error('Ошибка при создании:', error);
            message.error(error.message || 'Ошибка при создании кассы');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Title level={3} style={{ marginTop: 0 }}>
                Создание новой кассы
            </Title>

            <Form
                form={form}
                layout="horizontal"
                initialValues={{
                    is_virtual: 'no'
                }}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Основные атрибуты" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Название"
                                name="name"
                                rules={[
                                    { required: true, message: 'Введите название кассы' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите название кассы" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Внешний ID"
                                name="external_id"
                                rules={[
                                    { required: true, message: 'Введите внешний ID' }
                                ]}
                            >
                                <Input placeholder="Введите внешний ID" />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="НДС"
                                name="tax_rate"
                            >
                                <Select
                                    options={[
                                        { label: 'Без НДС', value: null },
                                        ...formData.tax_rate.map(rate => ({
                                            label: rate.label,
                                            value: rate.value
                                        }))
                                    ]}
                                    placeholder="Выберите НДС"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Ширина чека"
                                name="receipt_width"
                                rules={[
                                    { required: true, message: 'Выберите ширину чека' }
                                ]}
                            >
                                <Select
                                    options={formData.receipt_width.map(width => ({
                                        label: width.label,
                                        value: width.value
                                    }))}
                                    placeholder="Выберите ширину чека"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Виртуальная касса"
                                name="is_virtual"
                                rules={[
                                    { required: true, message: 'Выберите тип кассы' }
                                ]}
                            >
                                <Radio.Group>
                                    <Radio.Button value="yes">Да</Radio.Button>
                                    <Radio.Button value="no">Нет</Radio.Button>
                                </Radio.Group>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Принтер чеков"
                                name="receipt_printer_name"
                            >
                                <Input placeholder="Введите название принтера" />
                            </HorizontalFormItem>
                        </Card>

                        <Card title="Способы оплаты" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Доступные способы"
                                name="payment_methods"
                                rules={[
                                    { required: true, message: 'Выберите способы оплаты' }
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    options={formData.payment_methods_list.map(method => ({
                                        label: method.label || method.name,
                                        value: method.value || method.id
                                    }))}
                                    placeholder="Выберите способы оплаты"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Процент кэшбека бонусной системы"
                                name="customer_bonus_percent"
                                rules={[
                                    { required: true, message: 'Процент кэшбека бонусной системы' }
                                ]}
                            >
                                <InputNumber
                                    placeholder="Введите процент кэшбека бонусной системы"
                                    style={{ width: '100%' }}
                                    precision={2}
                                    min={0}
                                    max={999999999999.99} // 12 цифр до запятой и 2 после
                                    step={0.01}
                                    stringMode
                                />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Фискальные данные" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Фискальная касса"
                                name="fiscal_cash_id"
                                rules={[
                                    { required: true, message: 'Выберите фискальную кассу' }
                                ]}
                            >
                                <Select
                                    options={formData.fiscal_cash_registers.map(fiscal => ({
                                        label: fiscal.label || fiscal.name,
                                        value: fiscal.value || fiscal.id
                                    }))}
                                    placeholder="Выберите фискальную кассу"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>
                        </Card>

                        <Card title="Время продажи алкоголя" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Разрешенное время"
                                name="alcohol_sale_time"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value || value.length === 0) {
                                                return Promise.resolve(); // Поле не обязательное
                                            }

                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <AlcoholSaleTimeEditor />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CashRegisterCreate;